/**
 * EditEmployeeDialog — dual-mode dialog for the admin to CREATE or EDIT
 * an employee's core profile (nome, matricula, cpf, telefone, email,
 * posto_servico) without needing a spreadsheet upload.
 *
 * Props:
 *   open       (bool)                     — dialog visibility
 *   mode       ("edit" | "create")        — defaults to "edit"
 *   employeeId (string | null)            — required for mode="edit"
 *   onClose    ()                          — called on cancel / close
 *   onSaved    (result)                    — called after a successful save
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Loader2, Save, User, Hash, Fingerprint, Phone, Mail, MapPin, UserPlus,
  Stethoscope, AlertTriangle, CheckCircle2, Shield, Droplet, Briefcase,
  CalendarDays,
} from "lucide-react";
import { API, useAuth } from "../App";

const maskCpfInput = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const maskPhoneInput = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 13);
  let s = d;
  if (s.startsWith("55")) s = s.slice(2);
  if (s.length === 0) return "";
  if (s.length <= 2) return `(${s}`;
  if (s.length <= 7) return `(${s.slice(0, 2)}) ${s.slice(2)}`;
  if (s.length <= 11) {
    return `(${s.slice(0, 2)}) ${s.slice(2, 7)}-${s.slice(7)}`;
  }
  return `(${s.slice(0, 2)}) ${s.slice(2, 7)}-${s.slice(7, 11)}`;
};

const EMPTY_FORM = {
  nome: "", matricula: "", cpf: "",
  telefone: "", email: "", posto_servico: "",
  vencimento_aso: "",
  registro_dpf: "", tipo_sanguineo: "",
  cargo: "", data_admissao: "",
};

/**
 * Given an ISO date "yyyy-mm-dd", return status info:
 *   { level: "expired" | "soon" | "ok", days: N, label }
 */
const asoStatus = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return { level: "expired", days: -diff, label: `Vencido há ${-diff} dia(s)` };
  if (diff <= 30) return { level: "soon", days: diff, label: `Vence em ${diff} dia(s)` };
  return { level: "ok", days: diff, label: `Válido por ${diff} dia(s)` };
};

const isoToBr = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const brToIso = (br) => {
  if (!br) return "";
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br.trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : br;
};

const maskDateInput = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

export default function EditEmployeeDialog({
  open, employeeId, onClose, onSaved, mode = "edit",
}) {
  const { token } = useAuth();
  const isCreate = mode === "create";
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState(isCreate ? EMPTY_FORM : null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      setInitial(EMPTY_FORM);
      setForm(EMPTY_FORM);
      return;
    }
    if (!employeeId) return;
    setLoading(true);
    setInitial(null);
    axios
      .get(`${API}/admin/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      })
      .then((r) => {
        const emp = r.data || {};
        const nextForm = {
          nome: emp.nome || "",
          matricula: emp.matricula || "",
          cpf: maskCpfInput(emp.cpf || ""),
          telefone: maskPhoneInput(emp.telefone || ""),
          email: emp.email || "",
          posto_servico: emp.posto_servico || "",
          vencimento_aso: isoToBr(emp.vencimento_aso || ""),
          registro_dpf: emp.registro_dpf || "",
          tipo_sanguineo: emp.tipo_sanguineo || "",
          cargo: emp.cargo || emp.funcao || "",
          data_admissao: emp.data_admissao || emp.admissao || "",
        };
        setInitial(nextForm);
        setForm(nextForm);
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.detail || "Falha ao carregar funcionário",
        );
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [open, employeeId, token, onClose, isCreate]);

  const dirtyFields = (() => {
    if (!initial) return {};
    const out = {};
    if (form.nome !== initial.nome) out.nome = form.nome.trim();
    if (form.matricula !== initial.matricula) out.matricula = form.matricula.trim();
    if (form.cpf !== initial.cpf) out.cpf = form.cpf.replace(/\D/g, "");
    if (form.telefone !== initial.telefone) out.telefone = form.telefone.replace(/\D/g, "");
    if (form.email !== initial.email) out.email = form.email.trim();
    if (form.posto_servico !== initial.posto_servico) out.posto_servico = form.posto_servico.trim();
    if (form.vencimento_aso !== initial.vencimento_aso) {
      out.vencimento_aso = form.vencimento_aso.trim();
    }
    if (form.registro_dpf !== initial.registro_dpf) {
      out.registro_dpf = form.registro_dpf.trim();
    }
    if (form.tipo_sanguineo !== initial.tipo_sanguineo) {
      out.tipo_sanguineo = form.tipo_sanguineo.trim();
    }
    if (form.cargo !== initial.cargo) {
      out.cargo = form.cargo.trim();
    }
    if (form.data_admissao !== initial.data_admissao) {
      out.data_admissao = form.data_admissao.trim();
    }
    return out;
  })();

  const dirtyCount = Object.keys(dirtyFields).length;

  const canSubmit = isCreate
    ? form.nome.trim() && form.matricula.trim() && form.cpf.replace(/\D/g, "").length === 11
    : dirtyCount > 0;

  const save = async () => {
    if (!canSubmit) {
      toast.info(
        isCreate
          ? "Preencha nome, matrícula e CPF (11 dígitos)."
          : "Nenhuma alteração para salvar",
      );
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        const body = {
          nome: form.nome.trim(),
          matricula: form.matricula.trim(),
          cpf: form.cpf.replace(/\D/g, ""),
          telefone: form.telefone ? form.telefone.replace(/\D/g, "") : undefined,
          email: form.email ? form.email.trim() : undefined,
          posto_servico: form.posto_servico ? form.posto_servico.trim() : undefined,
          vencimento_aso: form.vencimento_aso ? form.vencimento_aso.trim() : undefined,
          registro_dpf: form.registro_dpf ? form.registro_dpf.trim() : undefined,
          tipo_sanguineo: form.tipo_sanguineo ? form.tipo_sanguineo.trim() : undefined,
          cargo: form.cargo ? form.cargo.trim() : undefined,
          data_admissao: form.data_admissao ? form.data_admissao.trim() : undefined,
        };
        const r = await axios.post(`${API}/admin/employees`, body, {
          headers: { Authorization: `Bearer ${token}` }, timeout: 20000,
        });
        toast.success(`Funcionário ${body.nome} cadastrado`);
        onSaved?.(r.data);
        onClose?.();
      } else {
        const r = await axios.patch(
          `${API}/admin/employees/${employeeId}`,
          dirtyFields,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
        );
        toast.success(
          `Dados atualizados (${(r.data?.updated_fields || [])
            .filter((f) => f !== "profile_updated_at").length} campo(s))`,
        );
        onSaved?.(r.data);
        onClose?.();
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail
          || (isCreate ? "Falha ao cadastrar" : "Falha ao salvar alterações"),
      );
    } finally { setSaving(false); }
  };

  const Icon = isCreate ? UserPlus : User;
  const title = isCreate ? "Novo funcionário" : "Editar dados do funcionário";
  const description = isCreate
    ? "Cadastre um novo funcionário sem precisar de planilha. Campos com * são obrigatórios."
    : "Atualize dados cadastrais, DPF e tipo sanguíneo sem reenviar planilha.";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="sm:max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid={isCreate ? "create-employee-dialog" : "edit-employee-dialog"}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-blue-700" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Carregando…
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="edit-emp-nome" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                Nome completo{isCreate ? " *" : ""}
              </Label>
              <Input
                id="edit-emp-nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value.toUpperCase() })}
                data-testid="edit-emp-nome"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-emp-matricula" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5" />
                  Matrícula{isCreate ? " *" : ""}
                </Label>
                <Input
                  id="edit-emp-matricula"
                  value={form.matricula}
                  onChange={(e) => setForm({ ...form, matricula: e.target.value.replace(/\s+/g, "") })}
                  data-testid="edit-emp-matricula"
                />
              </div>
              <div>
                <Label htmlFor="edit-emp-cpf" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                  <Fingerprint className="w-3.5 h-3.5" />
                  CPF{isCreate ? " *" : ""}
                </Label>
                <Input
                  id="edit-emp-cpf"
                  value={form.cpf}
                  onChange={(e) => setForm({ ...form, cpf: maskCpfInput(e.target.value) })}
                  placeholder="000.000.000-00"
                  data-testid="edit-emp-cpf"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-emp-posto" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Posto de Serviço
              </Label>
              <Input
                id="edit-emp-posto"
                value={form.posto_servico}
                onChange={(e) => setForm({ ...form, posto_servico: e.target.value.toUpperCase() })}
                placeholder="Ex: AVI CONTABILIDADE"
                data-testid="edit-emp-posto"
              />
            </div>

            <div>
              <Label htmlFor="edit-emp-admissao" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5" /> Data de Admissão
              </Label>
              <Input
                id="edit-emp-admissao"
                value={form.data_admissao}
                onChange={(e) => setForm({ ...form, data_admissao: maskDateInput(e.target.value) })}
                placeholder="dd/mm/aaaa"
                data-testid="edit-emp-admissao"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Usada na Ficha de EPI (data de entrega). Ao preencher, fichas emitidas sem a data são corrigidas automaticamente.
              </p>
            </div>

            {(() => {
              const st = asoStatus(brToIso(form.vencimento_aso));
              const badgeColor = st?.level === "expired"
                ? "bg-red-100 border-red-300 text-red-800"
                : st?.level === "soon"
                  ? "bg-amber-100 border-amber-300 text-amber-800"
                  : st?.level === "ok"
                    ? "bg-emerald-100 border-emerald-300 text-emerald-800"
                    : "";
              const inputBorder = st?.level === "expired"
                ? "border-red-400 focus-visible:ring-red-400"
                : st?.level === "soon"
                  ? "border-amber-400 focus-visible:ring-amber-400"
                  : "";
              const Ico = st?.level === "expired" ? AlertTriangle
                : st?.level === "soon" ? AlertTriangle
                  : st?.level === "ok" ? CheckCircle2 : Stethoscope;
              return (
                <div>
                  <Label htmlFor="edit-emp-vencaso" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                    <Stethoscope className="w-3.5 h-3.5" /> Vencimento do ASO
                  </Label>
                  <Input
                    id="edit-emp-vencaso"
                    value={form.vencimento_aso}
                    onChange={(e) => setForm({ ...form, vencimento_aso: maskDateInput(e.target.value) })}
                    placeholder="dd/mm/aaaa"
                    className={inputBorder}
                    data-testid="edit-emp-vencaso"
                  />
                  {st && (
                    <div className={`mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border rounded-full ${badgeColor}`}
                      data-testid="edit-emp-vencaso-badge">
                      <Ico className="w-3 h-3" />
                      {st.label}
                    </div>
                  )}
                  <p className="text-[10px] text-slate-500 mt-1">
                    Formato dd/mm/aaaa. Deixe em branco para remover.
                  </p>
                </div>
              );
            })()}

            {/* Cargo / Função */}
            <div>
              <Label htmlFor="edit-emp-cargo" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Cargo / Função
              </Label>
              <Input
                id="edit-emp-cargo"
                value={form.cargo}
                onChange={(e) => setForm({ ...form, cargo: e.target.value.toUpperCase() })}
                placeholder="Ex: VIGILANTE, ADMINISTRATIVO, PORTEIRO, RECEPCIONISTA..."
                list="cargo-suggestions"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                className="uppercase tracking-wide"
                data-testid="edit-emp-cargo"
              />
              <datalist id="cargo-suggestions">
                <option value="VIGILANTE" />
                <option value="ADMINISTRATIVO" />
                <option value="JOVEM APRENDIZ" />
                <option value="PORTEIRO" />
                <option value="RECEPCIONISTA" />
                <option value="SUPERVISOR" />
                <option value="INSPETOR" />
                <option value="LÍDER DE EQUIPE" />
                <option value="ANALISTA" />
                <option value="ASSISTENTE" />
                <option value="COORDENADOR" />
                <option value="GERENTE" />
                <option value="AUXILIAR DE LIMPEZA" />
                <option value="MOTORISTA" />
                <option value="ENGENHEIRO DE SEGURANÇA" />
              </datalist>
              <p className="text-[10px] text-slate-500 mt-1">
                Se vazio, é deduzido do posto de serviço (padrão VIGILANTE).
              </p>
            </div>

            {/* DPF + Tipo Sanguíneo (2 colunas) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-emp-dpf" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" /> Registro DPF
                </Label>
                <Input
                  id="edit-emp-dpf"
                  value={form.registro_dpf}
                  onChange={(e) => setForm({ ...form, registro_dpf: e.target.value.toUpperCase() })}
                  placeholder="Ex: BA-0075881"
                  data-testid="edit-emp-dpf"
                  className="font-mono"
                />
              </div>
              <div>
                <Label htmlFor="edit-emp-sang" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                  <Droplet className="w-3.5 h-3.5 text-red-500" /> Tipo Sanguíneo
                </Label>
                <select
                  id="edit-emp-sang"
                  value={form.tipo_sanguineo}
                  onChange={(e) => setForm({ ...form, tipo_sanguineo: e.target.value })}
                  data-testid="edit-emp-sang"
                  className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">—</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-emp-telefone" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> Telefone (com DDD)
              </Label>
              <Input
                id="edit-emp-telefone"
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: maskPhoneInput(e.target.value) })}
                placeholder="(71) 99999-9999"
                data-testid="edit-emp-telefone"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                {isCreate
                  ? "Opcional. Aceita fixo (10) ou celular (11 dígitos)."
                  : "Deixe em branco para remover."}
              </p>
            </div>

            <div>
              <Label htmlFor="edit-emp-email" className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> E-mail
              </Label>
              <Input
                id="edit-emp-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="funcionario@empresa.com"
                data-testid="edit-emp-email"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                {isCreate ? "Opcional." : "Deixe em branco para remover."}
              </p>
            </div>

            {!isCreate && dirtyCount > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 text-amber-900">
                <strong>{dirtyCount}</strong> campo(s) modificado(s) — clique em <em>Salvar</em> para persistir.
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline" onClick={() => onClose?.()}
            disabled={saving}
            data-testid="edit-emp-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading || !canSubmit}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="edit-emp-save"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isCreate ? "Cadastrando…" : "Salvando…"}
              </>
            ) : (
              <>
                {isCreate ? <UserPlus className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {isCreate ? "Cadastrar funcionário" : "Salvar alterações"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
